import { useCallback, useEffect, useMemo, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IntroPage from "../intro/intro";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import WizardStep from "../../components/wizard/wizard-step";
import { ParameterValue } from "../../common/types/parameters";
import OversiktligaResultat from "../modules/oversikt/oversikt";
import useWizard from "../../common/hooks/useWizard";
import { WizardStep as WizardStepType } from "../../common/hooks/useWizard";

function dependenciesAreResolved(
  step: WizardStepType,
  stepStates: WizardStepType[]
): boolean {
  let completedSteps = stepStates
    .filter((stepState) => stepState.isCompleted)
    .map((stepState) => stepState.id);
  return (
    step.inputParameters !== undefined &&
    step.inputParameters !== null &&
    step.inputParameters.length > 0 &&
    step.dependencies.every((dependency) => completedSteps.includes(dependency))
  );
}

function calculateInitalStep(
  sidebarSelection: string | undefined,
  wizardSteps: WizardStepType[]
): number {
  let initialStep = 0;
  if (sidebarSelection) {
    initialStep = parseInt(sidebarSelection);
  } else if (wizardSteps.some((step) => !step?.isCompleted)) {
    initialStep = wizardSteps.findIndex((step) => !step.isCompleted);
  }
  return initialStep;
}

function ControlledAccordions() {
  const { customer, economicProfile } = useParams();

  let { hash, pathname, search } = useLocation();
  const step = useMemo(() => hash.slice(1), [hash]);
  const navigate = useNavigate();

  const {
    steps: wizardSteps,
    setStepValue: setWizardStepValue,
    currentStepId,
    setCurrentStepId,
    wizardIsCompleted,
  } = useWizard(customer!, economicProfile!);

  const updateCurrentStep = useCallback(
    (stepNr: number) => {
      navigate(`${pathname}${search}#${stepNr}`, { replace: true });
    },
    [navigate, pathname, search]
  );

  useEffect(() => {
    // Decide which step to open (menu selection or the first uncompleted step)
    let initialStep = calculateInitalStep(step, wizardSteps);
    if (wizardSteps[initialStep]?.id !== currentStepId) {
      setCurrentStepId(wizardSteps[initialStep]?.id);
    }
  }, [currentStepId, setCurrentStepId, step, wizardSteps]);

  const [moduleParameterReloadNeeded, setModuleParameterReloadNeeded] =
    useState(false);

  function saveInput(index: number, stringifiedValues: ParameterValue[]) {
    let wizardStep = wizardSteps[index];

    let parameterReloadNeeded = wizardSteps.some((step) =>
      step.dependencies.includes(wizardStep.id)
    ); // profile change, may affect limits of other parameters, including other wizard parameters
    setModuleParameterReloadNeeded(parameterReloadNeeded && wizardIsCompleted);

    setWizardStepValue(wizardSteps[index].id, stringifiedValues);
    updateCurrentStep(index + 1);
  }

  return !step && !wizardSteps.some((step) => step.isCompleted) ? (
    <IntroPage />
  ) : (
    <div key="wizard-container">
      <Typography variant="h1">Inledande förutsättningar</Typography>
      {wizardSteps.map((step, index) => (
        <Accordion
          key={`wizard-step-${step.id}-accordion`}
          expanded={currentStepId === step.id}
          onChange={(event, expanded) => {
            if (expanded) {
              updateCurrentStep(index);
            } else {
              updateCurrentStep(index + 1);
            }
          }}
          elevation={0}
        >
          <AccordionSummary
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.03)",
              margin: "0px 0 0 0",
            }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel${index}bh-content`}
            id={`panel${index}bh-header`}
          >
            <Typography
              sx={{ width: "33%", flexShrink: 0 }}
              key={`wizard-step-${step.id}-title`}
            >
              {step.shortDisplayName}
            </Typography>
            <>{step.isCompleted && step.displayValue}</>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "rgba(0, 0, 0, 0.0)" }}>
            {dependenciesAreResolved(step, wizardSteps) ? (
              <WizardStep
                info={step}
                preSelected={step.inputParameters}
                onSelection={(val: ParameterValue[]) => {
                  saveInput(index, val);
                }}
              />
            ) : (
              "Vänligen vänta någon sekund för alternativen att visas. Om inga alternativ visas, kontrollera att du besvarat föregående steg."
            )}
          </AccordionDetails>
        </Accordion>
      ))}
      {wizardIsCompleted && (
        <OversiktligaResultat
          forceParameterReload={moduleParameterReloadNeeded}
          onReload={() => {
            setModuleParameterReloadNeeded(false);
          }}
          sx={{ marginTop: "40px" }}
        />
      )}
    </div>
  );
}

export { ControlledAccordions };
